// VRG-691 — the Sales forms. ONE component per object serves create AND edit (§30), and the
// header buttons, the drawer and the list menus all open these (§28): no second copy of a form.
// House components only: `@repo/ui` Modal / Button, platform Field / SelectField /
// TextAreaField / DatePickerField, FormSubmitButton for Save-until-dirty (§54), and
// ConfirmSettingModal for the confirm-past-evidence gate (§43).
import { useEffect, useMemo } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { Alert, Link, Stack, Typography } from '@mui/material'
import {
  DEAL_CLOSED_REASON_LABELS,
  DEFAULT_REPORTING_CURRENCY,
  DEFAULT_TAG_ECONOMICS,
  activationShareWhy,
  percentLabel,
  resolveActivationShare,
  TAG_COST_PLACEHOLDER_NOTE,
  TAG_MARKUP_MAX,
  TAG_UNIT_COST_MAX_USD,
  type TagEconomics,
  REPORTING_CURRENCIES,
  SALES_STAGES,
  CLIENT_TIERS,
  clientTierLabel,
  CLIENT_TIER_META,
  clientTierOptionLabel,
  type ClientTier,
  type CommercePlatform,
  FOUNDERS_TERM_SOON_DAYS,
  FOUNDERS_LIST_LIMITS,
  foundersTermDaysLeft,
  type FoundersListStatus,
  type DealClosedReason,
  type DealPlan,
  type DealSource,
  isSourceChannel,
  type SalesStage,
  type SourceChannel,
  type StageProbabilities,
  type SubSourceRef,
} from '@repo/types'
import { Button, Modal, Switch } from '@repo/ui'

import { DatePickerField, Field, FormSubmitButton, SelectField, TextAreaField } from 'components'
import { ConfirmSettingModal } from 'components/feedback/modals/ConfirmSettingModal/ConfirmSettingModal'
import { COMMERCE_PLATFORM_PICKER_OPTIONS } from 'constants/commercePlatforms'
import { SOURCE_CHANNEL_PICKER_OPTIONS } from 'constants/sourceChannels'
import type {
  SalesAccountInput,
  SalesAccountRow,
  SalesCreateDealInput,
  SalesDealCard,
  SalesDealInput,
  SalesLeadInput,
  SalesLeadRow,
  SalesPerson,
} from 'services'

import { INDUSTRY_OPTIONS } from '../GtmOutreachPage/outreachModals'
import { shortDate } from '../InternalAnalyticsPage/components/shared'
import {
  DEAL_SOURCE_OPTIONS,
  EVIDENCE_COPY,
  FOUNDERS_LIST_OPTIONS,
  INHERIT_SOURCE,
  moneyShort,
  OPEN_DEAL_WARNING,
  PLAN_OPTIONS,
  planLabel,
  STAGE_LABEL,
  stageList,
} from './dealsModel'
import type { PendingMove } from './DealsBoard'
import { SubSourceField } from './SubSourceField'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Untyped = any
const blank = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null)
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const MONEY = /^\d{1,12}(\.\d{1,2})?$/
const DAY = 'YYYY-MM-DD'

/**
 * VRG-701 — the client tier replaces the free "Size" buckets. Each option shows its range:
 * employees, typical certificates a year and the production cut-offs (approved 2026-10-14), all
 * from @repo/types gtm/client-tiers.ts.
 */
export const TIER_OPTIONS = [
  { value: '', label: 'Not set' },
  ...CLIENT_TIERS.map((t) => ({ value: t, label: clientTierOptionLabel(t), ...(CLIENT_TIER_META[t].note ? { description: CLIENT_TIER_META[t].note } : {}) })),
]
const CURRENCY_OPTIONS = REPORTING_CURRENCIES.map((c) => ({ value: c.code, label: `${c.code} · ${c.label}` }))
const staffOptions = (staff: readonly SalesPerson[]) => [{ value: '', label: 'Nobody' }, ...staff.map((s) => ({ value: s.id, label: s.name }))]
const withCurrent = (options: { value: string; label: string }[], current: string | null | undefined) =>
  current && !options.some((o) => o.value === current) ? [...options, { value: current, label: current }] : options

// ── Company ──────────────────────────────────────────────────────────────────────────────

type CompanyForm = {
  name: string
  domain: string
  industry: string
  tier: string
  /** VRG-716 — '' = not set. */
  commerce_platform: string
  /** VG-27 — '' = not on the Founder's List. */
  founders_list_status: string
  country: string
  city: string
  owner_user_id: string
  source_channel: string
  /** VRG-701 — a picked sub-source (was free text). */
  source_sub: SubSourceRef | null
  notes: string
}

/** The company fields, shared by the company form and the lead form's inline "new company". */
const CompanyFields = ({ m, staff, current }: { m: Untyped; staff: readonly SalesPerson[]; current?: SalesAccountRow | null }) => (
  <>
    <Field {...m} name="name" label="Company name" placeholder="Maison Vera" rules={{ required: 'Give the company a name' }} />
    <Field {...m} name="domain" label="Website (optional)" placeholder="maisonvera.com" />
    <Stack direction="row" spacing={3}>
      <Controller
        control={m.control}
        name="industry"
        render={({ field }) => (
          <SelectField label="Industry" placeholder="Not set" options={withCurrent(INDUSTRY_OPTIONS, current?.industry)} value={field.value} onChange={(v) => field.onChange(String(v))} />
        )}
      />
    </Stack>
    <TierField m={m} suggested={current?.suggested_tier ?? null} size={current?.size ?? null} />
    <CommercePlatformField m={m} current={current} />
    <FoundersListField m={m} />
    <LocationFields m={m} />
    <Controller
      control={m.control}
      name="owner_user_id"
      render={({ field }) => <SelectField label="Owner" placeholder="Nobody" options={staffOptions(staff)} value={field.value} onChange={(v) => field.onChange(String(v))} />}
    />
  </>
)

/**
 * VRG-701 — the client tier. When the company's ROI calculator gives annual production, the tier
 * it implies is SUGGESTED with a one-click "Use", never filled in for you; the old Size bucket, if
 * there was one, is shown so a person can map it by eye.
 */
const TierField = ({ m, suggested, size }: { m: Untyped; suggested: { tier: ClientTier; units: number } | null; size: string | null }) => {
  const value = m.watch('tier')
  return (
    <Stack spacing={1}>
      <Controller
        control={m.control}
        name="tier"
        render={({ field }) => <SelectField label="Tier" placeholder="Not set" options={TIER_OPTIONS} value={field.value ?? ''} onChange={(v) => field.onChange(String(v))} />}
      />
      {(suggested && suggested.tier !== value) || (size && !value) ? (
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }} data-testid="tier-suggestion">
          {suggested && suggested.tier !== value ? (
            <>
              The calculator says {suggested.units.toLocaleString('en-US')} units a year: {clientTierLabel(suggested.tier)}.{' '}
              <Link component="button" type="button" variant="body3" onClick={() => m.setValue('tier', suggested.tier, { shouldDirty: true })}>
                Use {clientTierLabel(suggested.tier)}
              </Link>
            </>
          ) : null}
          {size && !value ? ` Size was recorded as ${size} people.` : ''}
        </Typography>
      ) : null}
    </Stack>
  )
}

/**
 * VRG-716 — the storefront's commerce platform. A pick here is MANUAL and no later detection
 * replaces it; "Not set" hands it back to detection (the next demo made for this brand).
 */
const CommercePlatformField = ({ m, current }: { m: Untyped; current?: SalesAccountRow | null }) => {
  const value = m.watch('commerce_platform')
  const unchanged = (value ?? '') === (current?.commerce_platform ?? '')
  return (
    <Stack spacing={1}>
      <Controller
        control={m.control}
        name="commerce_platform"
        render={({ field }) => (
          <SelectField label="Commerce platform" placeholder="Not set" options={COMMERCE_PLATFORM_PICKER_OPTIONS} value={field.value ?? ''} onChange={(v) => field.onChange(String(v))} />
        )}
      />
      <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        {!unchanged
          ? value
            ? 'Saved as set by hand: a demo will not change it.'
            : 'Cleared: the next demo for this brand detects it again.'
          : current?.commerce_platform_source === 'detected'
            ? 'Detected from the site when a demo was made. Picking one here keeps it.'
            : current?.commerce_platform_source === 'manual'
              ? 'Set by hand. A demo will not change it.'
              : 'Making a demo detects it. Pick one if you know it.'}
      </Typography>
    </Stack>
  )
}

/**
 * VG-27 — the brand's place on the Founder's List. Deliberately on the COMPANY form and not the
 * deal: membership survives renewals, an expansion and a second deal, so a deal-level copy could
 * disagree with itself. The TERMS actually applied live on Edit deal, which the note points at.
 *
 * The note is here rather than in a tooltip because the consequence of this field is felt
 * somewhere else entirely — a pill on the Companies list, and a seat on a list capped by Spencer's
 * own calendar (§67: a setting that takes effect elsewhere must say where).
 */
const FoundersListField = ({ m }: { m: Untyped }) => (
  <Stack spacing={1}>
    <Controller
      control={m.control}
      name="founders_list_status"
      render={({ field }: { field: {value: string; onChange: (v: string) => void} }) => (
        <SelectField label="Founder's List" placeholder="Not on the list" options={FOUNDERS_LIST_OPTIONS} value={field.value ?? ''} onChange={(v) => field.onChange(String(v))} />
      )}
    />
    <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
      Internal only, and shown as a pill on Sales → Companies. Founding member is one of the first
      five. The terms actually applied (waived base, activation bank, term end) go on the deal.
    </Typography>
  </Stack>
)

/** VRG-696 — country + city, shared by the company form and the lead form's inline company. */
const LocationFields = ({ m }: { m: Untyped }) => (
  <Stack direction="row" spacing={3}>
    <Field {...m} name="country" label="Country" placeholder="US" rules={{ validate: (v: string) => !v?.trim() || /^[A-Za-z]{2}$/.test(v.trim()) || 'A two-letter code' }} />
    <Field {...m} name="city" label="City (optional)" placeholder="Chicago" />
  </Stack>
)


/**
 * Add or edit a SALES company — a brand we are selling to, not a platform tenant. The lead
 * source here is the MANUAL override: leave it unset and vera-2 fills it in where it can.
 */
export const SalesCompanyFormModal = ({
  open,
  account,
  staff,
  loading,
  onClose,
  onSave,
}: {
  open: boolean
  /** Absent = create. */
  account?: SalesAccountRow | null
  staff: readonly SalesPerson[]
  loading: boolean
  onClose: () => void
  onSave: (input: SalesAccountInput) => void
}) => {
  const methods = useForm<CompanyForm>()
  const { control, reset, handleSubmit } = methods
  const manual = account?.source.set_by === 'manual'
  useEffect(() => {
    if (!open) return
    reset({
      name: account?.name ?? '',
      domain: account?.domain ?? '',
      industry: account?.industry ?? '',
      tier: account?.tier ?? '',
      commerce_platform: account?.commerce_platform ?? '',
      founders_list_status: account?.founders_list_status ?? '',
      country: account?.country ?? '',
      city: account?.city ?? '',
      owner_user_id: account?.owner?.id ?? '',
      source_channel: manual ? (account?.source.channel ?? '') : '',
      source_sub: manual ? (account?.source.sub_source ?? null) : null,
      notes: account?.notes ?? '',
    })
  }, [open, account, manual, reset])
  const submit = handleSubmit((v) => {
    const sourceTouched = !!(blank(v.source_channel) || v.source_sub)
    onSave({
      name: v.name.trim(),
      domain: blank(v.domain),
      industry: blank(v.industry),
      tier: (blank(v.tier) as ClientTier | null) ?? null,
      // VRG-716 — only when changed: sending it records a MANUAL value, so an untouched detected
      // value must not ride along on every save and become "set by hand".
      ...((v.commerce_platform ?? '') !== (account?.commerce_platform ?? '')
        ? { commerce_platform: (blank(v.commerce_platform) as CommercePlatform | null) ?? null }
        : {}),
      founders_list_status: (blank(v.founders_list_status) as FoundersListStatus | null) ?? null,
      country: blank(v.country)?.toUpperCase() ?? null,
      city: blank(v.city),
      owner_user_id: blank(v.owner_user_id),
      notes: blank(v.notes),
      ...(sourceTouched
        ? {
            source_channel: (blank(v.source_channel) as SourceChannel | null) ?? null,
            source_sub: v.source_sub ? { kind: v.source_sub.kind, id: v.source_sub.id } : null,
          }
        : manual
          ? { source_reset: true }
          : {}),
    })
  })
  const m = methods as Untyped
  return (
    <Modal
      title={account ? 'Edit company' : 'Add company'}
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <FormSubmitButton control={control} requireDirty={!!account} onClick={() => submit()} loading={loading}>
            {account ? 'Save' : 'Add company'}
          </FormSubmitButton>
        </>
      }
    >
      <Stack spacing={4} mt={2}>
        <CompanyFields m={m} staff={staff} current={account} />
        <LeadSourceFields m={m} auto={account && !manual ? account.source : null} />
        <TextAreaField {...m} name="notes" label="Notes" placeholder="Where we met, what they make" />
      </Stack>
    </Modal>
  )
}

/**
 * Lead source: the channel plus a sub-source PICKED from that channel's list (VRG-701; it was free
 * text). Manual always wins over auto. Changing the channel clears the sub-source.
 */
const LeadSourceFields = ({ m, auto }: { m: Untyped; auto: SalesAccountRow['source'] | null }) => (
  <Stack spacing={2}>
    {/* Equal halves: a full-width SelectField beside the picker would squeeze it (§74). */}
    <Stack direction="row" spacing={3} sx={{ '& > *': { flex: '1 1 0', minWidth: 0 } }}>
      <Controller
        control={m.control}
        name="source_channel"
        render={({ field }) => (
          <SelectField
            label="Lead source channel"
            placeholder="Not set: Vera fills it in"
            options={SOURCE_CHANNEL_PICKER_OPTIONS}
            value={field.value}
            onChange={(v) => {
              field.onChange(String(v))
              m.setValue('source_sub', null, { shouldDirty: true })
            }}
          />
        )}
      />
      <Controller
        control={m.control}
        name="source_sub"
        render={({ field }) => {
          const channel = m.watch('source_channel')
          return (
            <SubSourceField
              channel={isSourceChannel(channel) ? channel : null}
              value={field.value ?? null}
              canManage
              onChange={(v) => field.onChange(v ? { ...v, label: '' } : null)}
            />
          )
        }}
      />
    </Stack>
    <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
      {auto?.detail || auto?.channel
        ? `Vera filled this in: ${auto.detail ?? ''}. Setting a channel or sub-source here replaces it; clearing both hands it back to Vera.`
        : 'Leave both empty and Vera fills them in where it can: outreach campaign, referral, tracked link or the website visit.'}
    </Typography>
  </Stack>
)

// ── Lead ─────────────────────────────────────────────────────────────────────────────────

type LeadForm = {
  email: string
  name: string
  title: string
  phone: string
  account_id: string
  notes: string
  // The inline "New company" fields.
  company_name?: string
  company_domain?: string
  industry?: string
  size?: string
  country?: string
  city?: string
  owner_user_id?: string
}

const NEW_COMPANY = '__new__'

/**
 * Add or edit a LEAD: a person. The email is the People key, so a lead whose email already
 * signed up is the same person as that signup and that People row. A lead can have no company;
 * "New company" creates one inline.
 */
export const LeadFormModal = ({
  open,
  lead,
  accounts,
  staff,
  defaultAccountId,
  loading,
  onClose,
  onSave,
}: {
  open: boolean
  lead?: SalesLeadRow | null
  accounts: readonly { id: string; name: string }[]
  staff: readonly SalesPerson[]
  defaultAccountId?: string | null
  loading: boolean
  onClose: () => void
  onSave: (input: SalesLeadInput) => void
}) => {
  const methods = useForm<LeadForm>()
  const { control, reset, handleSubmit, watch } = methods
  useEffect(() => {
    if (!open) return
    reset({
      email: lead?.email ?? '',
      name: lead?.name ?? '',
      title: lead?.title ?? '',
      phone: lead?.phone ?? '',
      account_id: lead?.account?.id ?? defaultAccountId ?? '',
      notes: lead?.notes ?? '',
      company_name: '',
      company_domain: '',
      industry: '',
      country: '',
      city: '',
      owner_user_id: '',
    })
  }, [open, lead, defaultAccountId, reset])
  const accountId = watch('account_id')
  const accountOptions = useMemo(
    () => [
      { value: '', label: 'No company' },
      { value: NEW_COMPANY, label: 'New company…' },
      ...accounts.map((a) => ({ value: a.id, label: a.name })),
    ],
    [accounts],
  )
  const submit = handleSubmit((v) =>
    onSave({
      email: v.email.trim().toLowerCase(),
      name: blank(v.name),
      title: blank(v.title),
      phone: blank(v.phone),
      notes: blank(v.notes),
      ...(v.account_id === NEW_COMPANY
        ? {
            new_account: {
              name: (v.company_name ?? '').trim(),
              domain: blank(v.company_domain),
              industry: blank(v.industry),
              size: blank(v.size),
              country: blank(v.country)?.toUpperCase() ?? null,
              city: blank(v.city),
              owner_user_id: blank(v.owner_user_id),
            },
          }
        : { account_id: v.account_id || null }),
    }),
  )
  const m = methods as Untyped
  return (
    <Modal
      title={lead ? 'Edit person' : 'Add person'}
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <FormSubmitButton control={control} requireDirty={!!lead} onClick={() => submit()} loading={loading}>
            {lead ? 'Save' : 'Add person'}
          </FormSubmitButton>
        </>
      }
    >
      <Stack spacing={4} mt={2}>
        <Field
          {...m}
          name="email"
          label="Email"
          placeholder="ada@maisonvera.com"
          rules={{ required: 'A person needs an email', validate: (v: string) => EMAIL.test(v.trim()) || 'Enter a valid email' }}
        />
        <Stack direction="row" spacing={3}>
          <Field {...m} name="name" label="Name" placeholder="Ada Lovelace" />
          <Field {...m} name="title" label="Title (optional)" placeholder="Head of brand protection" />
        </Stack>
        <Field {...m} name="phone" label="Phone (optional)" placeholder="+1 312 555 0100" />
        <Controller
          control={control}
          name="account_id"
          render={({ field }) => <SelectField label="Company" placeholder="No company" options={accountOptions} value={field.value} onChange={(v) => field.onChange(String(v))} />}
        />
        {accountId === NEW_COMPANY && (
          <Stack spacing={4} sx={{ pl: 4, borderLeft: (t) => `2px solid ${t.palette.primary[100]}` }}>
            <Field {...m} name="company_name" label="Company name" placeholder="Maison Vera" rules={{ required: 'Name the new company' }} />
            <Field {...m} name="company_domain" label="Website (optional, else the email's domain)" placeholder="maisonvera.com" />
            <Stack direction="row" spacing={3}>
              <Controller
                control={control}
                name="industry"
                render={({ field }) => <SelectField label="Industry" placeholder="Not set" options={INDUSTRY_OPTIONS} value={field.value ?? ''} onChange={(v) => field.onChange(String(v))} />}
              />
              <Controller
                control={control}
                name="owner_user_id"
                render={({ field }) => <SelectField label="Owner" placeholder="Nobody" options={staffOptions(staff)} value={field.value ?? ''} onChange={(v) => field.onChange(String(v))} />}
              />
            </Stack>
            <LocationFields m={m} />
          </Stack>
        )}
        <TextAreaField {...m} name="notes" label="Notes" placeholder="Met at the Chicago dinner" />
      </Stack>
    </Modal>
  )
}

// ── Deal ─────────────────────────────────────────────────────────────────────────────────

type DealForm = {
  account_id: string
  company_name: string
  company_domain: string
  lead_ids: string[]
  new_people: Array<{ email: string; name: string }>
  name: string
  stage: string
  amount: string
  currency: string
  expected_close_on: string
  owner_user_id: string
  next_step: string
  source: string
  /** VRG-701 — the sub-source under the deal's own channel. */
  source_sub: SubSourceRef | null
  plan: string
  /** VRG-701 — the deal's own calculator inputs (blank = the company's calculator). */
  calc_units: string
  calc_price: string
  calc_cac: string
  /** VRG-736 — share of production activated, PERCENT as typed (blank = the tier default). */
  activation_share: string
  /**
   * VG-27 — the Founder's List terms, as typed (blank = not set). `founders_bank_released` is a
   * toggle here and a timestamp in the database: the API stamps when, on the transition only, so
   * re-saving this form never moves the release date.
   */
  founders_waived_base_months: string
  founders_activation_bank: string
  founders_bank_released: boolean
  founders_term_ends_on: string
}

/**
 * VRG-701 — Add deal AND Edit deal: one form (§30). A deal at a company: its people, name, value
 * (the MANUAL figure always overrides the calculator and form ones), expected close, owner,
 * next step, source and plan.
 *
 * Create (no `deal`) adds the company and people pickers and the opening stage:
 *   - pick a company or create one inline; pick its people (or people with no company yet, who
 *     join it), and add new ones by email;
 *   - a company with another open deal that is not Won shows a WARNING, not a block (Spencer);
 *   - the source defaults to Expansion when the company is already a client (the API decides;
 *     the form says so), else the company's first touch;
 * Edit shows the plan read-only, "from Stripe", while a paying subscription sets it.
 */
export const DealFormModal = ({
  open,
  deal,
  accounts,
  leads,
  staff,
  defaultAccountId,
  loading,
  onClose,
  onSave,
  onCreate,
}: {
  open: boolean
  /** Absent = Add deal. */
  deal: SalesDealCard | null
  accounts: readonly SalesAccountRow[]
  /** Every lead: the people pickers offer those at the chosen company, and those with none. */
  leads: readonly SalesLeadRow[]
  staff: readonly SalesPerson[]
  defaultAccountId?: string | null
  loading: boolean
  onClose: () => void
  onSave: (input: SalesDealInput) => void
  onCreate: (input: SalesCreateDealInput) => void
}) => {
  const creating = !deal
  const methods = useForm<DealForm>()
  const { control, reset, handleSubmit, watch } = methods
  const people = useFieldArray({ control, name: 'new_people' })
  useEffect(() => {
    if (!open) return
    reset({
      account_id: deal?.account_id ?? defaultAccountId ?? '',
      company_name: '',
      company_domain: '',
      lead_ids: deal?.people_explicit ? deal.people.map((p) => p.id) : [],
      new_people: [],
      name: deal?.name ?? '',
      stage: 'lead',
      amount: deal?.value_manual ? String(deal.value_manual.amount) : '',
      currency: deal?.value_manual?.currency ?? DEFAULT_REPORTING_CURRENCY,
      expected_close_on: deal?.expected_close_on ?? '',
      owner_user_id: deal?.owner?.id ?? '',
      next_step: deal?.next_step ?? '',
      source: deal?.deal_source.value ?? INHERIT_SOURCE,
      source_sub: deal?.deal_source.value ? (deal.source.sub_source ?? null) : null,
      plan: deal?.plan_manual ?? '',
      calc_units: deal?.potential?.inputs.source === 'edited' && deal.potential.inputs.units != null ? String(deal.potential.inputs.units) : '',
      calc_price: deal?.potential?.inputs.source === 'edited' && deal.potential.inputs.price != null ? String(deal.potential.inputs.price) : '',
      calc_cac: deal?.potential?.inputs.source === 'edited' && deal.potential.inputs.cac != null ? String(deal.potential.inputs.cac) : '',
      activation_share: deal?.potential?.activation_share.source === 'deal' ? String(Math.round(deal.potential.activation_share.share * 1000) / 10) : '',
      founders_waived_base_months: deal?.founders_terms.waived_base_months != null ? String(deal.founders_terms.waived_base_months) : '',
      founders_activation_bank: deal?.founders_terms.activation_bank != null ? String(deal.founders_terms.activation_bank) : '',
      founders_bank_released: !!deal?.founders_terms.bank_released_at,
      founders_term_ends_on: deal?.founders_terms.term_ends_on ?? '',
    })
  }, [open, deal, defaultAccountId, reset])
  const accountId = watch('account_id')
  const newCompany = accountId === NEW_COMPANY
  const account = accounts.find((a) => a.id === accountId) ?? null
  const otherOpen = creating && !!account?.deals.some((d) => d.status === 'open' && d.stage !== 'paid_client')
  const isClient = creating && !!account?.deals.some((d) => d.status === 'open' && d.stage === 'paid_client')
  const sourceTouched = methods.formState.dirtyFields.source
  const accountOptions = useMemo(
    () => [{ value: NEW_COMPANY, label: 'New company…' }, ...accounts.map((a) => ({ value: a.id, label: a.name }))],
    [accounts],
  )
  const peopleOptions = useMemo(() => {
    const target = deal?.account_id ?? (newCompany ? null : accountId)
    return leads
      .filter((l) => (target ? l.account?.id === target || !l.account : !l.account))
      .map((l) => ({ value: l.id, label: l.name ? `${l.name} · ${l.email}` : l.email, group: l.account ? 'At this company' : 'No company yet' }))
  }, [leads, deal, accountId, newCompany])
  const stripePlan = deal?.stripe_plan ?? null
  // The company's calculator figures, as placeholders (only when the deal is not already edited).
  const calcDefault = deal?.potential?.inputs.source === 'calculator' ? deal.potential.inputs : null
  // VRG-736 — what a blank share means for THIS deal: its tier's default (or the suggested tier's).
  const shareDefault =
    deal?.potential && deal.potential.activation_share.source !== 'deal'
      ? deal.potential.activation_share
      : resolveActivationShare({ tier: (deal?.tier ?? account?.tier) ?? null, units: deal?.potential?.inputs.units ?? null })

  const submit = handleSubmit((v) => {
    const common: SalesDealInput = {
      name: blank(v.name),
      value_manual: blank(v.amount) ? { amount: Number(v.amount.trim()), currency: v.currency || DEFAULT_REPORTING_CURRENCY } : null,
      expected_close_on: blank(v.expected_close_on),
      owner_user_id: blank(v.owner_user_id),
      ...(stripePlan ? {} : { plan: (blank(v.plan) as DealPlan | null) ?? null }),
      // VRG-701 — all three blank = the company's calculator ("from calculator").
      calc_inputs:
        blank(v.calc_units) || blank(v.calc_price) || blank(v.calc_cac)
          ? {
              units: blank(v.calc_units) ? Number(v.calc_units.replace(/[,\s]/g, '')) : null,
              price: blank(v.calc_price) ? Number(v.calc_price.replace(/[$,\s]/g, '')) : null,
              cac: blank(v.calc_cac) ? Number(v.calc_cac.replace(/[$,\s]/g, '')) : null,
            }
          : null,
      // VRG-736 — percent typed, ratio sent; blank = back to the tier default.
      activation_share: blank(v.activation_share) ? Number(v.activation_share.replace(/[%\s]/g, '')) / 100 : null,
      // VG-27 — the Founder's List terms. Clearing the bank clears its release server-side, so the
      // toggle is sent as typed and never has to be second-guessed here.
      founders_waived_base_months: blank(v.founders_waived_base_months) ? Number(v.founders_waived_base_months.replace(/[,\s]/g, '')) : null,
      founders_activation_bank: blank(v.founders_activation_bank) ? Number(v.founders_activation_bank.replace(/[,\s]/g, '')) : null,
      founders_bank_released: !!v.founders_bank_released,
      founders_term_ends_on: blank(v.founders_term_ends_on),
    }
    if (creating) {
      const newPeople = (v.new_people ?? []).filter((p) => p.email.trim())
      onCreate({
        ...common,
        ...(newCompany
          ? { new_account: { name: v.company_name.trim(), domain: blank(v.company_domain) } }
          : { account_id: v.account_id }),
        lead_ids: v.lead_ids ?? [],
        new_leads: newPeople.map((p) => ({ email: p.email.trim().toLowerCase(), name: blank(p.name) })),
        stage: (v.stage || 'lead') as SalesStage,
        // Untouched: the API applies the default (Expansion at a client, else inherit).
        ...(sourceTouched ? { source: (blank(v.source) as DealSource | null) ?? null } : {}),
        source_sub: v.source_sub ? { kind: v.source_sub.kind, id: v.source_sub.id } : null,
      })
    } else {
      onSave({
        ...common,
        next_step: blank(v.next_step),
        source: (blank(v.source) as DealSource | null) ?? null,
        source_sub: v.source_sub ? { kind: v.source_sub.kind, id: v.source_sub.id } : null,
        lead_ids: v.lead_ids ?? [],
      })
    }
  })
  const m = methods as Untyped
  const auto = deal && deal.value.basis !== 'manual' ? deal.value : null
  return (
    <Modal
      title={creating ? 'Add deal' : 'Edit deal'}
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <FormSubmitButton control={control} requireDirty={!creating} onClick={() => submit()} loading={loading}>
            {creating ? 'Add deal' : 'Save'}
          </FormSubmitButton>
        </>
      }
    >
      <Stack spacing={4} mt={2} data-testid="deal-form">
        {creating && (
          <>
            <Controller
              control={control}
              name="account_id"
              rules={{ required: 'Pick a company or add a new one' }}
              render={({ field, fieldState }) => (
                <Stack spacing={1}>
                  <SelectField label="Company" placeholder="Pick a company" search options={accountOptions} value={field.value} onChange={(v) => field.onChange(String(v))} />
                  {fieldState.error && (
                    <Typography variant="body3" color="error.main">
                      {fieldState.error.message}
                    </Typography>
                  )}
                </Stack>
              )}
            />
            {newCompany && (
              <Stack spacing={4} sx={{ pl: 4, borderLeft: (t) => `2px solid ${t.palette.primary[100]}` }}>
                <Field {...m} name="company_name" label="Company name" placeholder="Maison Vera" rules={{ required: 'Name the new company' }} />
                <Field {...m} name="company_domain" label="Website (optional)" placeholder="maisonvera.com" />
              </Stack>
            )}
            {otherOpen && (
              <Alert severity="warning" sx={{ lineHeight: 1.5 }} data-testid="deal-form-open-warning">
                {OPEN_DEAL_WARNING}
              </Alert>
            )}
          </>
        )}
        <Controller
          control={control}
          name="lead_ids"
          render={({ field }) => (
            <SelectField
              label={creating ? 'People (optional)' : 'People'}
              placeholder={deal && !deal.people_explicit ? `The company's people (${deal.people.length})` : 'Nobody picked'}
              multiple
              search={peopleOptions.length > 6}
              options={peopleOptions}
              value={field.value ?? []}
              onChange={(v) => field.onChange(Array.isArray(v) ? v : [v])}
            />
          )}
        />
        {creating && (
          <Stack spacing={3}>
            {people.fields.map((f, i) => (
              <Stack key={f.id} direction="row" spacing={3} alignItems="flex-end">
                <Field
                  {...m}
                  name={`new_people.${i}.email`}
                  label={`New person ${i + 1}: email`}
                  placeholder="ada@maisonvera.com"
                  rules={{ validate: (x: string) => !x?.trim() || EMAIL.test(x.trim()) || 'Enter a valid email' }}
                />
                <Field {...m} name={`new_people.${i}.name`} label="Name (optional)" placeholder="Ada Lovelace" />
              </Stack>
            ))}
            <Link component="button" type="button" variant="body3" onClick={() => people.append({ email: '', name: '' })} sx={{ alignSelf: 'flex-start' }}>
              + Add a new person
            </Link>
          </Stack>
        )}
        <Stack direction="row" spacing={3}>
          <Field {...m} name="name" label="Deal name (optional)" placeholder="Rollout to all stores" />
          {creating && (
            <Controller
              control={control}
              name="stage"
              render={({ field }) => (
                <SelectField
                  label="Stage"
                  options={SALES_STAGES.map((s) => ({ value: s, label: STAGE_LABEL[s] }))}
                  value={field.value}
                  onChange={(v) => field.onChange(String(v))}
                />
              )}
            />
          )}
        </Stack>
        <Stack direction="row" spacing={3}>
          <Field
            {...m}
            name="amount"
            label="Predicted ARR (manual)"
            placeholder={auto?.amount != null ? String(auto.amount) : '24000'}
            rules={{ validate: (v: string) => !v?.trim() || MONEY.test(v.trim()) || 'A number, up to two decimals' }}
          />
          <Controller
            control={control}
            name="currency"
            render={({ field }) => <SelectField label="Currency" options={CURRENCY_OPTIONS} value={field.value} onChange={(v) => field.onChange(String(v))} />}
          />
        </Stack>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {auto?.amount != null
            ? `Without a manual figure the deal uses ${auto.detail ?? 'its derived value'}. A manual figure always wins for ARR (tag margin is still calculated from the inputs); only USD figures count toward the pipeline totals.`
            : 'Without a manual figure the deal takes its value from the calculator or the waitlist form when the company has one. A manual figure always wins for ARR (tag margin is still calculated from the inputs); only USD figures count toward the pipeline totals.'}
          {deal?.forecast?.snapshot
            ? ` The prediction was frozen ${shortDate(deal.forecast.snapshot.taken_at)}, so accuracy keeps comparing against that one; a new figure changes the live value only.`
            : ''}
        </Typography>
        <Stack direction="row" spacing={3}>
          <Controller
            control={control}
            name="source"
            render={({ field }) => (
              <SelectField
                label="Source"
                placeholder={creating && isClient && !sourceTouched ? 'Expansion (the default: already a client)' : "Company's first touch"}
                search
                options={DEAL_SOURCE_OPTIONS}
                value={field.value ?? ''}
                onChange={(v) => {
                  field.onChange(String(v))
                  methods.setValue('source_sub', null, { shouldDirty: true })
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="plan"
            render={({ field }) => (
              <SelectField
                label={stripePlan ? 'Plan (from Stripe)' : 'Plan'}
                placeholder="Not set"
                disabled={!!stripePlan}
                options={stripePlan ? [{ value: stripePlan, label: planLabel(stripePlan) }] : PLAN_OPTIONS}
                value={stripePlan ?? field.value ?? ''}
                onChange={(v) => field.onChange(String(v))}
              />
            )}
          />
        </Stack>
        {isSourceChannel(watch('source')) && (
          <Controller
            control={control}
            name="source_sub"
            render={({ field }) => (
              <SubSourceField
                channel={watch('source') as SourceChannel}
                value={field.value ?? null}
                canManage
                onChange={(v) => field.onChange(v ? { ...v, label: '' } : null)}
              />
            )}
          />
        )}
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {creating && isClient && !sourceTouched
            ? 'This company is already a client, so the deal starts as Expansion. Pick a channel instead if this sale came from one.'
            : "Company's first touch credits the channel that brought the company in. Expansion marks a later sale to an existing client."}
          {stripePlan ? ' The plan comes from the paying subscription in Stripe and changes with it.' : ''}
        </Typography>
        <Stack direction="row" spacing={3}>
          <DatePickerField {...m} name="expected_close_on" label="Expected close (optional)" format={DAY} />
          <Controller
            control={control}
            name="owner_user_id"
            render={({ field }) => <SelectField label="Owner" placeholder="Nobody" options={staffOptions(staff)} value={field.value} onChange={(v) => field.onChange(String(v))} />}
          />
        </Stack>
        {!creating && <Field {...m} name="next_step" label="Next step" placeholder="Send the pilot proposal" />}
        <Stack spacing={2} data-testid="deal-form-calc">
          <Typography variant="body3" sx={{ fontWeight: 500 }}>
            Calculator inputs
          </Typography>
          <Stack direction="row" spacing={3}>
            <Field
              {...m}
              name="calc_units"
              label="Products a year"
              placeholder={calcDefault?.units != null ? calcDefault.units.toLocaleString('en-US') : '12,000'}
              rules={{ validate: (x: string) => !x?.trim() || /^[1-9][\d,]*$/.test(x.trim()) || 'A whole number above 0' }}
            />
            <Field {...m} name="calc_price" label="Average price (USD)" placeholder={calcDefault?.price != null ? String(calcDefault.price) : '850'} rules={{ validate: (x: string) => !x?.trim() || MONEY.test(x.replace(/[$,\s]/g, '')) || 'A number' }} />
            <Field {...m} name="calc_cac" label="CAC (USD)" placeholder={calcDefault?.cac != null ? String(calcDefault.cac) : 'Optional'} rules={{ validate: (x: string) => !x?.trim() || MONEY.test(x.replace(/[$,\s]/g, '')) || 'A number' }} />
          </Stack>
          <Field
            {...m}
            name="activation_share"
            label="Share of production activated (%)"
            placeholder={`${percentLabel(shareDefault.share)} (${activationShareWhy(shareDefault)})`}
            rules={{ validate: (x: string) => !x?.trim() || (/^\d{1,3}(\.\d{1,2})?%?$/.test(x.trim()) && Number(x.replace('%', '')) <= 100) || 'A percentage between 0 and 100' }}
          />
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {calcDefault
              ? "Blank fields use the company's ROI-calculator figures (shown greyed). Typing any of them marks the deal's inputs as edited, and they price its potential revenue and its value (Deal inputs basis)."
              : "The company has no ROI-calculator submission. Typing products a year prices the deal's potential revenue and its value (Deal inputs basis)."}{' '}
            Only the share of production activated is priced as activations; blank uses the tier&apos;s default (Tier 1–2 pilot a slice of
            their range, Tier 3–5 tag everything). Tag margin on the same units is shown beside the ARR, never in it.
          </Typography>
        </Stack>
        <FoundersTermsFields m={m} control={control} />
      </Stack>
    </Modal>
  )
}

/**
 * VG-27 — the Founder's List terms AS APPLIED TO THIS DEAL. Whether the brand is on the list is a
 * COMPANY field (Edit company): membership survives renewals, an expansion and a second deal, so a
 * deal-level copy of it could disagree with itself. What belongs here is the one agreement with one
 * end date — which is why a second deal at the same brand carries its own term, or none.
 *
 * Blank is the normal case: most deals are not on these terms, so nothing here is required and
 * nothing defaults.
 *
 * The term-end note is the point of storing that date at all. The canon names month 7 as the
 * classic churn moment — the base fee reappears and the plan moves off Business to whatever fits
 * the brand's volume — and it is only manageable if it is visible BEFORE it lands.
 */
const FoundersTermsFields = ({ m, control }: { m: Untyped; control: Untyped }) => {
  const termEnds = m.watch('founders_term_ends_on') as string | undefined
  const bank = m.watch('founders_activation_bank') as string | undefined
  const hasBank = !!blank(bank ?? '')
  const daysLeft = foundersTermDaysLeft(blank(termEnds ?? '') ? (termEnds as string) : null)
  // Clearing the bank turns the toggle off, so the form never SENDS the contradiction. The API
  // resolves it too (clearing a bank clears its release), but a DISABLED switch still reading "on"
  // is a lie on screen, and that half no server rule can fix.
  //
  // `m` is in the deps because the lint rule wants it, and that is harmless whether or not
  // react-hook-form hands back a stable object: the body is guarded and idempotent, so a re-run
  // with the field already false does nothing. Correctness here does not rest on the reference.
  useEffect(() => {
    if (!hasBank && m.getValues('founders_bank_released')) {
      m.setValue('founders_bank_released', false, { shouldDirty: true })
    }
  }, [hasBank, m])
  return (
    <Stack spacing={2} data-testid="deal-form-founders">
      <Typography variant="body3" sx={{ fontWeight: 500 }}>
        Founder&apos;s List terms
      </Typography>
      <Stack direction="row" spacing={3}>
        <Field
          {...m}
          name="founders_waived_base_months"
          label="Base fee waived (months)"
          placeholder="6"
          rules={{
            validate: (x: string) =>
              !x?.trim() ||
              (/^\d{1,2}$/.test(x.trim()) && Number(x) > 0 && Number(x) <= FOUNDERS_LIST_LIMITS.maxWaivedBaseMonths) ||
              `A whole number of months, 1 to ${FOUNDERS_LIST_LIMITS.maxWaivedBaseMonths}`,
          }}
        />
        <Field
          {...m}
          name="founders_activation_bank"
          label="Activation bank"
          placeholder="Sized to the first run"
          rules={{
            validate: (x: string) =>
              !x?.trim() ||
              (/^[1-9][\d,]*$/.test(x.trim()) && Number(x.replace(/,/g, '')) <= FOUNDERS_LIST_LIMITS.maxActivationBank) ||
              'A whole number of activations above 0',
          }}
        />
      </Stack>
      <Stack direction="row" spacing={3}>
        <DatePickerField {...m} name="founders_term_ends_on" label="Term ends (optional)" format={DAY} />
      </Stack>
      {/* A boolean is a Switch (§30), and it is disabled until there is a bank to release — which is
          the same rule the API and the CHECK in 0171 both hold: a release needs a bank. */}
      <Stack direction="row" spacing={3} alignItems="center" justifyContent="space-between">
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography variant="body3">Activation bank released</Typography>
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {hasBank
              ? 'Released on delivery of the named items, not at signing. The date is stamped once and does not move when you save again.'
              : 'Give the bank a size first.'}
          </Typography>
        </Stack>
        <Controller
          control={control}
          name="founders_bank_released"
          render={({ field }: { field: {value: boolean; onChange: (v: boolean) => void} }) => (
            <Switch checked={!!field.value && hasBank} disabled={!hasBank} onChange={() => field.onChange(!field.value)} />
          )}
        />
      </Stack>
      <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        {daysLeft === null
          ? 'Blank on an ordinary deal. The term end is what makes the handover visible before it arrives: the base fee resumes that day and the plan moves to whatever fits their volume.'
          : daysLeft < 0
            ? `The term ended ${Math.abs(daysLeft)} ${Math.abs(daysLeft) === 1 ? 'day' : 'days'} ago. The base fee has resumed — check the plan still fits their volume.`
            : daysLeft <= FOUNDERS_TERM_SOON_DAYS
              ? `The term ends in ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}. The base fee resumes then, and the plan moves to whatever fits their volume — the conversation to have now.`
              : `The term ends in ${daysLeft} days. The base fee resumes then, and the plan moves to whatever fits their volume.`}
      </Typography>
    </Stack>
  )
}

/** Close a deal as Lost or Archived. Reversible: Reopen sits in the drawer. */
export const CloseDealModal = ({
  deal,
  loading,
  onClose,
  onConfirm,
  lostOnly = false,
}: {
  deal: SalesDealCard | null
  loading: boolean
  onClose: () => void
  onConfirm: (reason: DealClosedReason, note: string | null) => void
  /** VRG-692 — opened by dropping a card on the Lost column: the reason is Lost, only a note is asked. */
  lostOnly?: boolean
}) => {
  const methods = useForm<{ reason: DealClosedReason; note: string }>()
  const { control, reset, watch, getValues } = methods
  useEffect(() => {
    if (deal) reset({ reason: 'lost', note: '' })
  }, [deal, reset])
  const reason = watch('reason')
  const m = methods as Untyped
  return (
    <ConfirmSettingModal
      open={!!deal}
      onClose={onClose}
      onConfirm={() => onConfirm(getValues('reason'), blank(getValues('note')))}
      title={lostOnly ? `Mark ${deal?.account_name ?? 'the deal'} lost` : `Close ${deal?.account_name ?? 'deal'}`}
      consequence={
        lostOnly
          ? `It moves to the Lost column, and its ${deal?.value.amount != null ? `${moneyShort(deal.value.amount, deal.value.currency)} ` : ''}predicted ARR leaves Open pipeline and Weighted pipeline. Its history stays, and it shows as Lost on the flow chart. Drag it back out of Lost to reopen it.`
          : 'The deal leaves the board and its value leaves the pipeline totals. Its history stays, and you can reopen it from the company.'
      }
      confirmLabel={reason === 'archived' ? 'Archive deal' : 'Mark lost'}
      loading={loading}
    >
      <Stack spacing={4} sx={{ mt: 2 }}>
        {!lostOnly && <Controller
          control={control}
          name="reason"
          render={({ field }) => (
            <SelectField
              label="Reason"
              options={(['lost', 'archived'] as const).map((r) => ({ value: r, label: DEAL_CLOSED_REASON_LABELS[r] }))}
              value={field.value}
              onChange={(v) => field.onChange(String(v))}
            />
          )}
        />}
        <Field {...m} name="note" label={lostOnly ? 'Reason (optional)' : 'Note (optional)'} placeholder="Chose a competitor" />
      </Stack>
    </ConfirmSettingModal>
  )
}

/**
 * The gate a drag PAST the evidence opens (Spencer: "manual drags never move a deal past
 * evidence silently"). It names what Vera has not seen, and confirmed moves are marked manual.
 */
export const ConfirmStageMoveModal = ({
  move,
  loading,
  onClose,
  onConfirm,
}: {
  move: PendingMove | null
  loading: boolean
  onClose: () => void
  onConfirm: () => void
}) => {
  const missing = move?.check.kind === 'confirm' ? move.check.missing : []
  return (
    <ConfirmSettingModal
      open={!!move}
      onClose={onClose}
      onConfirm={onConfirm}
      title={move ? `Move ${move.deal.account_name} to ${STAGE_LABEL[move.to]}?` : 'Move deal'}
      consequence={
        <>
          Vera has no evidence for {stageList(missing)} on this deal yet. It will be recorded as set by hand, and it counts in
          the pipeline totals as if it were true until the evidence arrives.
        </>
      }
      confirmLabel={`Move to ${move ? STAGE_LABEL[move.to] : ''}`}
      loading={loading}
    >
      <Stack component="ul" spacing={1} sx={{ m: 0, mt: 2, pl: 5 }}>
        {missing.map((s) =>
          s === 'lead' ? null : (
            <Typography component="li" variant="body3" key={s} sx={{ lineHeight: 1.5 }}>
              {STAGE_LABEL[s]} needs {EVIDENCE_COPY[s]}.
            </Typography>
          ),
        )}
      </Stack>
    </ConfirmSettingModal>
  )
}

type ProbForm = Record<(typeof SALES_STAGES)[number], string>

/** Stage probabilities for the weighted value, in percent. */
export const StageProbabilitiesModal = ({
  open,
  value,
  loading,
  onClose,
  onSave,
}: {
  open: boolean
  value: StageProbabilities
  loading: boolean
  onClose: () => void
  onSave: (p: StageProbabilities) => void
}) => {
  const methods = useForm<ProbForm>()
  const { control, reset, handleSubmit } = methods
  useEffect(() => {
    if (!open) return
    reset(Object.fromEntries(SALES_STAGES.map((s) => [s, String(Math.round(value[s] * 1000) / 10)])) as ProbForm)
  }, [open, value, reset])
  const submit = handleSubmit((v) =>
    onSave(Object.fromEntries(SALES_STAGES.map((s) => [s, Number(v[s]) / 100])) as StageProbabilities),
  )
  const m = methods as Untyped
  return (
    <Modal
      title="Stage probabilities"
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <FormSubmitButton control={control} requireDirty onClick={() => submit()} loading={loading}>
            Save
          </FormSubmitButton>
        </>
      }
    >
      <Stack spacing={4} mt={2}>
        <Alert severity="info" sx={{ lineHeight: 1.5 }}>
          The weighted value multiplies each open deal&apos;s value by its stage&apos;s chance of closing. A deal in the Demo lane uses
          its own stage.
        </Alert>
        {SALES_STAGES.map((s) => (
          <Field
            key={s}
            {...m}
            name={s}
            label={`${STAGE_LABEL[s]} (%)`}
            rules={{
              required: 'Give a percentage',
              validate: (x: string) => (/^\d{1,3}(\.\d)?$/.test(x.trim()) && Number(x) <= 100) || 'Between 0 and 100',
            }}
          />
        ))}
      </Stack>
    </Modal>
  )
}


type TagEconomicsForm = { unit_cost_usd: string; markup: string }

/**
 * VRG-736 — Sales settings for the tag-margin line, beside Stage probabilities: the cost of one
 * tag and the markup on it. Both blank = the coded PLACEHOLDERS (`DEFAULT_TAG_ECONOMICS`), and the
 * unit cost stays "placeholder until the supply price list" whatever is typed (VRG-143 replaces it
 * with the price list per form factor).
 */
export const TagEconomicsModal = ({
  open,
  value,
  loading,
  onClose,
  onSave,
}: {
  open: boolean
  value: TagEconomics
  loading: boolean
  onClose: () => void
  onSave: (input: { unit_cost_usd: number | null; markup: number | null }) => void
}) => {
  const methods = useForm<TagEconomicsForm>()
  const { control, reset, handleSubmit } = methods
  useEffect(() => {
    if (!open) return
    reset({
      unit_cost_usd: value.unit_cost_source === 'setting' ? String(value.unit_cost_usd) : '',
      markup: value.markup_source === 'setting' ? String(Math.round(value.markup * 1000) / 10) : '',
    })
  }, [open, value, reset])
  const submit = handleSubmit((v) =>
    onSave({
      unit_cost_usd: v.unit_cost_usd.trim() ? Number(v.unit_cost_usd.replace(/[$,\s]/g, '')) : null,
      markup: v.markup.trim() ? Number(v.markup.replace(/[%\s]/g, '')) / 100 : null,
    }),
  )
  const m = methods as Untyped
  return (
    <Modal
      title="Tag economics"
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <FormSubmitButton control={control} requireDirty onClick={() => submit()} loading={loading}>
            Save
          </FormSubmitButton>
        </>
      }
    >
      <Stack spacing={4} mt={2}>
        <Alert severity="warning" sx={{ lineHeight: 1.5 }} data-testid="tag-economics-placeholder">
          Placeholders. A deal&apos;s tag margin is its expected activations × the tag unit cost × the markup, shown beside its ARR and
          never in it. The unit cost is a {TAG_COST_PLACEHOLDER_NOTE}; leave a field blank to use the placeholder.
        </Alert>
        <Field
          {...m}
          name="unit_cost_usd"
          label="Tag unit cost (USD)"
          placeholder={`$${DEFAULT_TAG_ECONOMICS.unit_cost_usd.toFixed(2)} (placeholder)`}
          rules={{
            validate: (x: string) =>
              !x?.trim() || (/^\$?\d+(\.\d{1,4})?$/.test(x.trim()) && Number(x.replace('$', '')) <= TAG_UNIT_COST_MAX_USD) || `A dollar amount up to ${TAG_UNIT_COST_MAX_USD}`,
          }}
        />
        <Field
          {...m}
          name="markup"
          label="Tag markup (%)"
          placeholder={`${percentLabel(DEFAULT_TAG_ECONOMICS.markup)} (placeholder)`}
          rules={{
            validate: (x: string) =>
              !x?.trim() || (/^\d{1,4}(\.\d{1,2})?%?$/.test(x.trim()) && Number(x.replace('%', '')) <= TAG_MARKUP_MAX * 100) || `A percentage up to ${TAG_MARKUP_MAX * 100}`,
          }}
        />
      </Stack>
    </Modal>
  )
}
